/**
 * Links-collection layer — the field/item module for the `record_links` grid,
 * parallel to `records.ts` / `listings.ts`. Owns the item type and the grid's
 * column fields, the single layer feeding a link collection its items + fields.
 */

import { type AugerField, type EnrichableField, getFieldType } from "../fields/fields.js"
import { getUserById } from "../registries/users.js"
import { type BaseItem } from "./item.js"

/** A `record_links` row as the links grid reads it. The reference columns carry
 *  both the stored id (`*_id`) and its resolved entity (`record`/`linked`/`user`)
 *  — the resolved one is `value` (display), the id is the raw value the editor
 *  binds. Open contract: extra joined columns may ride along per query. */
export type LinkItem = BaseItem & {
  id: number
  record_id?: number
  linked_id?: number
  actor_person_id?: number | null
  record?: unknown
  linked?: unknown
  created_at?: string
  updated_at?: string
}

const row = (item: unknown) => item as LinkItem

/** Column specs for the links grid. Reference columns split `value` (resolved
 *  entity, for display) from `rawValue` (the stored id the editor binds);
 *  scalar columns leave `rawValue` to default to `value`. */
const FIELDS: ReadonlyArray<EnrichableField> = [
  { key: "id", label: "ID", type: "number", width: 80, value: (i) => row(i).id },
  {
    key: "_record",
    label: "Source Record",
    type: "record",
    width: 200,
    value: (i) => row(i).record,
    rawValue: (i) => row(i).record_id,
  },
  {
    key: "linked",
    label: "Target Record",
    type: "record",
    width: 200,
    value: (i) => row(i).linked,
    rawValue: (i) => row(i).linked_id,
  },
  {
    // The person who made the link (the actor pair's person half), resolved
    // through the staff directory. A `user` field stores a person record id.
    key: "actor_person_id",
    label: "Created By",
    type: "user",
    width: 150,
    value: (i) => {
      const id = row(i).actor_person_id
      return id == null ? null : (getUserById(id) ?? id)
    },
    rawValue: (i) => row(i).actor_person_id,
  },
  {
    key: "updated_at",
    label: "Updated At",
    type: "timestamp",
    width: 180,
    value: (i) => row(i).updated_at,
  },
  {
    key: "created_at",
    label: "Created At",
    type: "timestamp",
    width: 180,
    value: (i) => row(i).created_at,
  },
]

/** The links grid's enriched fields: each spec's FieldType chain resolved (so it
 *  carries that type's format/parse/operators) and `rawValue` guaranteed —
 *  defaulting to `value` for scalar columns that don't split id from entity. */
export const linkFields: AugerField[] = FIELDS.map(
  (f) =>
    ({
      ...getFieldType(f.type),
      ...f,
      rawValue: f.rawValue ?? f.value,
    }) as AugerField
)
